// Fachlogik der Projekt-Freischaltung und der Begehungsauftraege
// (ERP/CRM-Plan, Abschnitt 7 „Workflow" Schritte 1–2 — Phase D2).
//
// Hier steht ausschliesslich, was sich OHNE Datenbank entscheiden laesst:
// welcher Statuswechsel erlaubt ist, welcher Auftrag noch laeuft, ob sich zwei
// Zeitraeume ueberschneiden. Der unreine Teil (Laden, RPC-Aufrufe) liegt in
// assignmentService.ts.
//
// Kein jsdom im Projekt (Standing Decision) — deshalb pur und mit Vitest
// geprueft.
//
// Tabellen, RLS und RPCs: supabase/freelancer_assignments.sql
// (MIGRATIONS.md Nr. 19).

// ─── Typen (camelCase-Spiegel der Tabellen) ─────────────────────────────────

/**
 * Lebenszyklus eines Begehungsauftrags. Spiegel des CHECK in
 * freelancer_assignments.sql — dieselbe Reihenfolge, dieselben neun Werte.
 */
export type AssignmentStatus =
    | 'angeboten'
    | 'angenommen'
    | 'abgelehnt'
    | 'storniert'
    | 'durchgefuehrt'
    | 'bericht_eingereicht'
    | 'nachbesserung'
    | 'geprueft'
    | 'abgerechnet';

/** Reihenfolge = fachlicher Ablauf, nicht Alphabet. */
export const ASSIGNMENT_STATUS: AssignmentStatus[] = [
    'angeboten',
    'angenommen',
    'abgelehnt',
    'storniert',
    'durchgefuehrt',
    'bericht_eingereicht',
    'nachbesserung',
    'geprueft',
    'abgerechnet',
];

export interface InspectionAssignment {
    id: string;
    companyId: string;
    freelancerId: string;
    projectId: string;
    /** ISO-Datum (YYYY-MM-DD), Beginn EINGESCHLOSSEN. */
    zeitraumVon: string;
    /** ISO-Datum (YYYY-MM-DD), Ende EINGESCHLOSSEN. */
    zeitraumBis: string;
    /** Verguetung in CENT — nie in Euro rechnen. Kopie des Satzes zum
     *  Zeitpunkt der Beauftragung, keine Live-Referenz. */
    verguetungCents: number;
    status: AssignmentStatus;
    /** Eingereichter Bericht (D4). In D2 durchgehend undefined. */
    reportId?: string;
    createdAt: string;
}

export interface FreelancerProjectAccess {
    id: string;
    companyId: string;
    freelancerId: string;
    projectId: string;
    grantedBy: string;
    grantedAt: string;
    /** Gesetzt = entzogen. Die Zeile bleibt als Historie stehen. */
    revokedAt?: string;
}

// ─── Vokabular ──────────────────────────────────────────────────────────────

/** Anzeigetexte. `Record` erzwingt einen Eintrag je Schluessel. */
export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
    angeboten: 'Angeboten',
    angenommen: 'Angenommen',
    abgelehnt: 'Abgelehnt',
    storniert: 'Storniert',
    durchgefuehrt: 'Durchgeführt',
    bericht_eingereicht: 'Bericht eingereicht',
    nachbesserung: 'Nachbesserung',
    geprueft: 'Geprüft',
    abgerechnet: 'Abgerechnet',
};

/**
 * Tailwind-Klassen der Statusabzeichen. Die UI waehlt nie selbst eine Farbe
 * (gleiche Regel wie QUALI_STATUS_KLASSEN in freelancerLogic.ts).
 *
 * Farbfamilien nach AUSSAGE, nicht nach Reihenfolge: blau = wartet auf eine
 * Entscheidung, indigo/emerald = laeuft bzw. ist erledigt, slate = folgenlos
 * beendet, amber = braucht Zustimmung oder Nacharbeit.
 */
export const ASSIGNMENT_STATUS_KLASSEN: Record<AssignmentStatus, string> = {
    angeboten: 'bg-blue-50 text-blue-700 border-blue-200',
    angenommen: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    abgelehnt: 'bg-slate-50 text-slate-500 border-slate-200',
    storniert: 'bg-slate-50 text-slate-500 border-slate-200',
    durchgefuehrt: 'bg-sky-50 text-sky-700 border-sky-200',
    bericht_eingereicht: 'bg-violet-50 text-violet-700 border-violet-200',
    nachbesserung: 'bg-amber-50 text-amber-700 border-amber-200',
    geprueft: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    abgerechnet: 'bg-teal-50 text-teal-700 border-teal-200',
};

/**
 * Zustaende, in denen ein Auftrag den Zeitraum des Freelancers im Projekt
 * BELEGT. Genau diese Liste steht im WHERE-Teil des EXCLUDE-Constraints
 * `inspection_assignments_no_overlap` (freelancer_assignments.sql, 1c) — wer
 * sie hier aendert, muss sie dort aendern.
 *
 * Storniert, abgelehnt und abgerechnet fehlen bewusst: ein zurueckgezogener
 * oder laengst abgerechneter Auftrag darf keinen neuen blockieren.
 */
export const AKTIVE_ASSIGNMENT_STATUS: AssignmentStatus[] = [
    'angeboten',
    'angenommen',
    'durchgefuehrt',
    'bericht_eingereicht',
    'nachbesserung',
];

/** Wer den Wechsel ausloest. In D2 gibt es nur `admin` in der Oberflaeche. */
export type AssignmentActor = 'admin' | 'freelancer';

/**
 * Uebergangsmatrix: wer darf aus welchem Status wohin?
 *
 * SPIEGEL von `public.set_assignment_status` in
 * supabase/freelancer_assignments.sql (Abschnitt 6d). Die Datenbank ist die
 * REGEL, diese Tabelle ist die BEQUEMLICHKEIT — sie steuert, welche Knoepfe
 * ueberhaupt erscheinen. Wer eine Seite aendert, muss die andere mitaendern.
 *
 * Bewusst NICHT enthalten: durchgefuehrt / bericht_eingereicht /
 * nachbesserung / geprueft (Phase D4) und abgerechnet (D5). Diese Uebergaenge
 * brauchen fachliche Nebenbedingungen, die es noch nicht gibt; die RPC wirft
 * dafuer eine sprechende Ausnahme.
 */
export const ASSIGNMENT_TRANSITIONS: Record<
    AssignmentActor,
    Partial<Record<AssignmentStatus, AssignmentStatus[]>>
> = {
    admin: {
        angeboten: ['storniert'],
        angenommen: ['storniert'],
    },
    freelancer: {
        angeboten: ['angenommen', 'abgelehnt'],
    },
};

/** Welche Folgestatus stehen diesem Handelnden hier offen? Nie `undefined`. */
export const erlaubteFolgestatus = (
    actor: AssignmentActor,
    status: AssignmentStatus | null | undefined,
): AssignmentStatus[] => {
    if (!status) return [];
    return ASSIGNMENT_TRANSITIONS[actor]?.[status] ?? [];
};

/** Ist dieser Wechsel fuer diesen Handelnden erlaubt? */
export const istErlaubterUebergang = (
    actor: AssignmentActor,
    von: AssignmentStatus | null | undefined,
    nach: AssignmentStatus,
): boolean => erlaubteFolgestatus(actor, von).includes(nach);

// ─── Zustandsfragen ─────────────────────────────────────────────────────────

/**
 * Belegt der Auftrag noch einen Zeitraum? Beantwortet zugleich „zaehlt er als
 * offener Auftrag, wenn eine Freischaltung entzogen werden soll?".
 */
export const istAktiverAuftrag = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && AKTIVE_ASSIGNMENT_STATUS.includes(a.status);

/**
 * Darf der Innendienst diesen Auftrag stornieren? Spiegel der Admin-Zeile der
 * Matrix — als eigene Funktion, weil die Oberflaeche genau diese Frage an
 * jeder Auftragszeile stellt.
 */
export const canAdminCancel = (
    a: Pick<InspectionAssignment, 'status'> | null | undefined,
): boolean => !!a && istErlaubterUebergang('admin', a.status, 'storniert');

/** Ist die Freischaltung aktiv (nicht entzogen)? */
export const istAktiveFreischaltung = (
    z: Pick<FreelancerProjectAccess, 'revokedAt'> | null | undefined,
): boolean => !!z && !z.revokedAt;

// ─── Zeitraeume ─────────────────────────────────────────────────────────────

/**
 * Ein ISO-Datum als Tageszahl seit der Epoche. `Date.UTC` statt `new Date(s)`,
 * damit die Zeitzone nicht mitrechnet — gleiche Begruendung wie in
 * freelancerLogic.ts. null bei allem, was kein Datum ist.
 */
const tagesZahl = (iso?: string | null): number | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim());
    if (!m) return null;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isFinite(ms)) return null;
    return Math.round(ms / 86_400_000);
};

/** Ein ISO-Datum deutsch ("05.09.2026"); '' bei allem, was kein Datum ist. */
export const formatDatum = (iso?: string | null): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/**
 * Zeitraum als Klartext. Ein Ein-Tages-Auftrag wird NICHT als „05.09.2026 –
 * 05.09.2026" geschrieben: die Wiederholung liest sich wie ein Fehler.
 *
 * Fehlt eine Seite, wird die vorhandene allein gezeigt — lieber halb als
 * gar nicht, denn eine leere Zelle sagt dem Sachbearbeiter nichts.
 */
export const formatZeitraum = (von?: string | null, bis?: string | null): string => {
    const a = formatDatum(von);
    const b = formatDatum(bis);
    if (a && b) return a === b ? a : `${a} – ${b}`;
    return a || b || '';
};

/** Dauer in Tagen, BEIDE Enden eingeschlossen (5.–7. = 3 Tage). */
export const zeitraumTage = (von?: string | null, bis?: string | null): number | null => {
    const a = tagesZahl(von);
    const b = tagesZahl(bis);
    if (a === null || b === null || b < a) return null;
    return b - a + 1;
};

/**
 * Ueberschneiden sich zwei geschlossene Zeitraeume? Gleiche Semantik wie
 * `daterange(von, bis, '[]') && daterange(…)` in der Datenbank: beide Enden
 * eingeschlossen, Beruehrung am selben Tag IST eine Ueberschneidung.
 */
export const zeitraeumeUeberschneidenSich = (
    aVon?: string | null,
    aBis?: string | null,
    bVon?: string | null,
    bBis?: string | null,
): boolean => {
    const a1 = tagesZahl(aVon);
    const a2 = tagesZahl(aBis);
    const b1 = tagesZahl(bVon);
    const b2 = tagesZahl(bBis);
    if (a1 === null || a2 === null || b1 === null || b2 === null) return false;
    return a1 <= b2 && b1 <= a2;
};

/** Der geplante Auftrag, bevor es ihn gibt. */
export interface AssignmentEntwurf {
    /** Bei einer Bearbeitung die eigene id — sie darf sich nicht selbst
     *  blockieren. In D2 immer undefined (es gibt nur Neuanlage). */
    id?: string;
    freelancerId: string;
    projectId: string;
    zeitraumVon: string;
    zeitraumBis: string;
}

/**
 * Welche bestehenden Auftraege stehen diesem Entwurf im Weg?
 *
 * Gleiche Semantik wie der EXCLUDE-Constraint
 * `inspection_assignments_no_overlap`: derselbe Freelancer, dasselbe Projekt,
 * ein AKTIVER Status und ueberlappende, an beiden Enden geschlossene
 * Zeitraeume.
 *
 * Der Sinn ist SOFORTIGES Feedback im Dialog — der Sachbearbeiter soll die
 * Kollision sehen, bevor er speichert, statt hinterher eine Fehlermeldung zu
 * lesen. Die Entscheidung faellt trotzdem in der Datenbank: nur sie sieht
 * auch die Auftraege, die dieser Client gerade nicht geladen hat.
 *
 * Bewusst NUR je Freelancer+Projekt: derselbe Mensch kann in derselben Woche
 * zwei verschiedene Baustellen begehen — das ist der Normalfall.
 *
 * Rueckgabe nach Beginn sortiert, damit die Warnung immer gleich aussieht.
 */
export const assignmentKonflikte = (
    neu: AssignmentEntwurf | null | undefined,
    bestehende: InspectionAssignment[] | null | undefined,
): InspectionAssignment[] => {
    if (!neu?.freelancerId || !neu?.projectId) return [];
    return (bestehende || [])
        .filter(
            (a) =>
                !!a &&
                a.id !== neu.id &&
                a.freelancerId === neu.freelancerId &&
                a.projectId === neu.projectId &&
                istAktiverAuftrag(a) &&
                zeitraeumeUeberschneidenSich(
                    neu.zeitraumVon,
                    neu.zeitraumBis,
                    a.zeitraumVon,
                    a.zeitraumBis,
                ),
        )
        .sort((a, b) => a.zeitraumVon.localeCompare(b.zeitraumVon));
};

// ─── Auswertung ─────────────────────────────────────────────────────────────

/**
 * Zaehlt die Auftraege, die ein Entzug der Freischaltung beruehren wuerde
 * (Warnhinweis im ProjectAccessPanel). Die Aufteilung folgt
 * `revoke_project_access`: angebotene werden automatisch storniert,
 * angenommene bleiben stehen und muessen von Hand geklaert werden.
 */
export interface EntzugsFolgen {
    /** Werden beim Entzug automatisch storniert. */
    werdenStorniert: number;
    /** Bleiben bestehen — der Innendienst muss sie einzeln klaeren. */
    bleibenOffen: number;
}

export const entzugsFolgen = (
    freelancerId: string,
    projectId: string,
    assignments: InspectionAssignment[] | null | undefined,
): EntzugsFolgen => {
    let werdenStorniert = 0;
    let bleibenOffen = 0;
    for (const a of assignments || []) {
        if (!a || a.freelancerId !== freelancerId || a.projectId !== projectId) continue;
        if (a.status === 'angeboten') werdenStorniert += 1;
        else if (a.status === 'angenommen') bleibenOffen += 1;
    }
    return { werdenStorniert, bleibenOffen };
};

/** Summe der Verguetungen in Cent — die Kostenzeile eines Projekts. */
export const summeVerguetungCents = (
    assignments: InspectionAssignment[] | null | undefined,
    nurStatus?: AssignmentStatus[],
): number =>
    (assignments || []).reduce((summe, a) => {
        if (!a) return summe;
        if (nurStatus && !nurStatus.includes(a.status)) return summe;
        return summe + (Number.isFinite(a.verguetungCents) ? a.verguetungCents : 0);
    }, 0);

/**
 * Auftraege in die Reihenfolge bringen, in der sie gelesen werden: das, was
 * als naechstes ansteht, zuerst — also nach Beginn ABSTEIGEND (der juengste
 * Auftrag oben), bei gleichem Beginn nach Anlage.
 */
export const sortiereAssignments = (
    assignments: InspectionAssignment[] | null | undefined,
): InspectionAssignment[] =>
    [...(assignments || [])].sort((a, b) => {
        const v = (b.zeitraumVon || '').localeCompare(a.zeitraumVon || '');
        if (v !== 0) return v;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
